// Kabukicho north strip: a narrow snack-bar front and a back-of-block roof prop. Kind names are prefixed with the region.
import { K, NEON, px, disc, frame, shadow, dk, lt, alpha, text, textW } from './hd-kit';
import type { G } from './hd-kit';
import { registerFront, registerRoofProp } from './hd-registry';
import type { LightFn } from './hd-registry';
import { door, display, lantern, bandSign, fitS, plant } from './hd-parts';

// A row of tiny snack bars: each a narrow lit door under its own lantern, signs stacked on the wall above.
registerFront('kabukiNorthSnack', { wall: dk(K.pink, 0.62), wallAc: true }, ({ g, x, y, w, h, gf, gy, sign, L }) => {
  if (sign) bandSign(g, x + w / 2, y + 12, sign.text, sign.color, L, fitS(sign.text, w));
  const n = Math.max(2, Math.floor(w / 64));
  for (let i = 0; i < n; i++) {
    const sx = x + 8 + i * ((w - 16) / n), col = i % 2 ? NEON.cyan : NEON.pink;
    door(g, sx + 10, gy + 6, 26, gf - 8, col, L);
    frame(g, sx + 7, gy + 3, 32, gf - 3, col);
    lantern(g, sx + 42, gy + 8, i % 2 ? K.amber : K.red, L);
    const t = i % 2 ? 'BAR' : 'スナック', tw = textW(t, 1) + 4;
    px(g, sx + 8, gy - 12, tw, 10, K.ink); text(g, t, sx + 10, gy - 10, col, 1);
  }
  plant(g, x + w - 18, gy + gf - 20);
});

// Robot Restaurant: a giant robot mascot between two signs, rows of chasing bulbs, and two Oni plaques (it fronts Oni jobs).
function oniMask(g: G, x: number, y: number, L: LightFn) {
  px(g, x - 1, y - 1, 22, 24, K.ink); px(g, x, y, 20, 22, K.red);
  px(g, x + 1, y + 1, 18, 3, K.pink); // brow
  px(g, x - 2, y - 4, 4, 6, K.white); px(g, x + 18, y - 4, 4, 6, K.white); // horns
  px(g, x + 3, y + 6, 5, 3, K.ink); px(g, x + 12, y + 6, 5, 3, K.ink); px(g, x + 4, y + 7, 3, 1, K.amber); px(g, x + 13, y + 7, 3, 1, K.amber);
  px(g, x + 3, y + 14, 14, 5, K.ink);
  for (let i = 0; i < 4; i++) px(g, x + 4 + i * 3, y + 14, 2, 3, K.white);
  L(x, y, 20, 22, K.pink, 0.4, true);
}
function bulbs(g: G, x: number, y: number, w: number, L: LightFn) {
  const cols = [K.pink, K.amber, K.cyan];
  for (let i = 0, bx = x; bx < x + w - 3; i++, bx += 8) {
    const c = cols[i % 3];
    px(g, bx, y, 4, 4, K.ink); px(g, bx + 1, y + 1, 2, 2, lt(c, 0.5)); L(bx, y, 4, 4, c, 0.18);
  }
}
registerFront('kabukiNorthRobot', { wall: dk(K.cyan, 0.86), upper: 'none' }, ({ g, x, y, w, gf, gy, L }) => {
  const cx = x + Math.floor(w / 2), top = y + 10;
  px(g, x, y + 2, w, 8, K.ink); bulbs(g, x + 2, y + 4, w - 4, L); // marquee
  px(g, x, gy - 8, w, 8, K.ink); bulbs(g, x + 2, gy - 6, w - 4, L);
  // mascot: antenna, head, grille mouth, shoulders and raised fists
  px(g, cx - 1, top - 2, 3, 8, K.metalHi); disc(g, cx, top - 3, 3, 3, K.red); L(cx - 3, top - 5, 6, 6, K.pink, 0.3, false, true);
  px(g, cx - 19, top + 6, 38, 28, K.ink); px(g, cx - 18, top + 7, 36, 26, K.metal); px(g, cx - 18, top + 7, 36, 3, K.metalHi); px(g, cx + 14, top + 10, 4, 22, K.metalLo);
  for (const ex of [-13, 3]) { px(g, cx + ex, top + 13, 11, 8, K.ink); px(g, cx + ex + 1, top + 14, 9, 6, K.cyan); px(g, cx + ex + 3, top + 15, 3, 2, K.white); L(cx + ex, top + 13, 11, 8, K.cyan, 0.3, false, true); }
  px(g, cx - 12, top + 25, 24, 6, K.ink); for (let i = 0; i < 6; i++) px(g, cx - 11 + i * 4, top + 26, 2, 4, i % 2 ? K.amber : K.metalHi);
  px(g, cx - 26, top + 36, 52, 8, K.ink); px(g, cx - 25, top + 37, 50, 6, K.red); px(g, cx - 25, top + 37, 50, 2, K.pink);
  for (const s of [-1, 1]) { const ax = s < 0 ? cx - 38 : cx + 26; px(g, ax, top + 12, 12, 32, K.ink); px(g, ax + 1, top + 13, 10, 30, K.metalLo); px(g, ax + 1, top + 13, 3, 30, K.metal); px(g, ax - 1, top + 4, 14, 12, K.ink); px(g, ax, top + 5, 12, 10, K.pink); }
  // signs either side
  bandSign(g, x + 52, top + 6, 'ROBOT', 'pink', L, 2);
  bandSign(g, x + w - 52, top + 6, 'ロボット', 'cyan', L, 2);
  text(g, 'RESTAURANT', x + 12, top + 34, K.amber, 1);
  L(x + 12, top + 34, textW('RESTAURANT', 1), 6, K.amber, 0.2, false, true);
  // ground floor: lit door between two Oni plaques and two glowing displays
  door(g, cx - 18, gy + 4, 36, gf - 14, K.pink, L); frame(g, cx - 21, gy + 1, 42, gf - 11, K.pink);
  oniMask(g, x + 14, gy + 2, L); oniMask(g, x + w - 34, gy + 2, L);
  display(g, x + 46, gy + 6, 44, gf - 20, K.cyan, L, false); display(g, x + w - 90, gy + 6, 44, gf - 20, K.amber, L, false);
});

// stacked exhaust pipes and a grease bin: the back of a kitchen
registerRoofProp('kabukiNorthStack', ({ g, x, y, L }) => {
  shadow(g, x + 4, y + 10, 24, 16, 0.4);
  px(g, x, y + 14, 22, 12, K.ink); px(g, x + 1, y + 15, 20, 10, K.metal); px(g, x + 1, y + 15, 20, 2, K.metalHi);
  for (const dx of [3, 11]) { px(g, x + dx, y, 6, 16, K.ink); px(g, x + dx + 1, y + 1, 2, 14, K.metalHi); px(g, x + dx + 3, y + 1, 2, 14, K.metalLo); disc(g, x + dx + 3, y, 3, 2, K.metalLo); }
  L(x + 4, y + 20, 14, 3, K.amber, 0.2);
});

/** Decal for the Robot Restaurant fixer kiosk, painted flat on the sidewalk beside its counter (hd px, (cx, cy) = the counter):
 *  a robot-head totem sign to the east and a pink/cyan chevron strip with ROBOT below. Decorative only. */
export function paintRobotKioskDecal(g: G, cx: number, cy: number) {
  const tx = cx + 38, ty = cy - 22;
  px(g, tx - 1, ty - 1, 26, 34, K.ink); px(g, tx, ty, 24, 32, K.metalLo); px(g, tx, ty, 24, 3, K.metalHi);
  px(g, tx + 11, ty - 7, 2, 7, K.metalHi); px(g, tx + 9, ty - 10, 6, 5, K.red);
  for (const ex of [3, 13]) { px(g, tx + ex, ty + 8, 8, 6, K.ink); px(g, tx + ex + 1, ty + 9, 6, 4, K.cyan); px(g, tx + ex + 2, ty + 10, 2, 1, K.white); }
  px(g, tx + 4, ty + 19, 16, 5, K.ink); for (let i = 0; i < 4; i++) px(g, tx + 5 + i * 4, ty + 20, 2, 3, i % 2 ? K.amber : K.metalHi);
  px(g, tx + 2, ty + 26, 20, 4, K.pink); frame(g, tx - 1, ty - 1, 26, 34, K.pink);
  const sy = cy - 20, sx = cx - 96;
  for (let i = 0; i < 48; i += 8) { px(g, sx + i, sy, 4, 6, K.pink); px(g, sx + 4 + i, sy, 4, 6, K.cyan); }
  px(g, sx - 2, sy + 8, textW('ROBOT', 1) + 4, 9, K.ink); text(g, 'ROBOT', sx, sy + 10, K.pink, 1);
}